import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Tooltip,
} from "@heroui/react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { resolveTimeOffTypeName } from "@/pages/Dashboard/Schedule/Components/TimeOffTypes/timeOffTypeDisplayName";
import { UNTYPED_TYPE_COLOR } from "@/pages/Dashboard/Schedule/Components/TimeOffTypes/TimeOffTypeTiles";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import {
  type CalendarShift,
  type CalendarTimeOff,
  useCalendarStore,
} from "../../store/useCalendarStore";
import {
  type DayAbsenceDescription,
  describeDayAbsence,
  resolveDayAbsence,
} from "../hooks/dayAbsence";
import { useOpenTimeOff } from "../hooks/useOpenTimeOff";

type HeaderUser = {
  userId: number;
  name?: string | null;
  image?: string | null;
  description?: string | null;
};

// `rows` are this person's day-level time off, `shifts` their shifts.
export function StaffHeaderCell({
  user,
  date,
  rows,
  shifts,
  reservesBadgeSlot,
}: {
  user: HeaderUser;
  date: string;
  rows: CalendarTimeOff[];
  shifts: CalendarShift[];
  reservesBadgeSlot: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const {
    setSelectedAppointmentId,
    setLastSelectedUserId,
    setLastSelectedResourceId,
    setLastSelectedDate,
    setLastSelectedTime,
    setNewBreakData,
  } = useCalendarStore(
    useShallow((state) => ({
      setSelectedAppointmentId: state.setSelectedAppointmentId,
      setLastSelectedUserId: state.setLastSelectedUserId,
      setLastSelectedResourceId: state.setLastSelectedResourceId,
      setLastSelectedDate: state.setLastSelectedDate,
      setLastSelectedTime: state.setLastSelectedTime,
      setNewBreakData: state.setNewBreakData,
    })),
  );
  const showName = useCalendarStore(
    (state) => state.filterItems.showStaffNames,
  );
  // The same check `/time-off/over-shifts` makes.
  const canAddAbsence = useUserPermissionsStore(
    (state: { permissions: { view_availability?: boolean | number } }) =>
      !!state.permissions.view_availability,
  );

  const isFiltered = params.get("u") === String(user.userId);
  const absence = resolveDayAbsence(rows, shifts, date);

  function addAbsence() {
    setSelectedAppointmentId(null);
    setLastSelectedUserId(user.userId);
    setLastSelectedResourceId(null);
    setLastSelectedDate(date);
    setLastSelectedTime(null);
    setNewBreakData({
      userId: user.userId,
      date,
      startTime: "09:00",
      granularity: "DAY",
      opensTimeOffTab: true,
    });
  }

  function toggleFilter() {
    // Merge: passing an object would drop every other param (date, mode, …).
    setParams((prev) => {
      if (prev.has("u")) prev.delete("u");
      else prev.set("u", String(user.userId));
      return prev;
    });
  }

  return (
    <div className="flex w-full min-w-0 flex-col items-center gap-0.5">
      <Tooltip
        placement="bottom"
        showArrow
        content={
          <div className="flex max-w-75 flex-col items-center">
            <p className="font-medium">{user.name}</p>
            {user.description ? (
              <p className="text-secondary-text text-sm">{user.description}</p>
            ) : null}
          </div>
        }
      >
        <div>
          <Dropdown placement="bottom">
            <DropdownTrigger>
              <Button
                variant="light"
                className="shrink-0 cursor-pointer data-[hover=true]:bg-transparent"
                disableRipple
                data-identifier="calendar-staff-menu"
              >
                <Avatar
                  name={user.name ?? ""}
                  src={user.image ?? undefined}
                  getInitials={(name) =>
                    name
                      .split(" ")
                      .map((word) => word[0])
                      .join("")
                      .slice(0, 2)
                  }
                  showFallback
                  color="primary"
                  classNames={{
                    base: "bg-[#ebf8ef] cursor-pointer shrink-0 w-[34px] h-[34px]",
                    icon: "text-[#74b587]",
                    name: "text-[#74b587] font-semibold",
                  }}
                />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label={t`Zaposleni`}
              onAction={(key) => {
                if (key === "filter") toggleFilter();
                else if (key === "absence") addAbsence();
              }}
            >
              <DropdownItem
                key="filter"
                data-identifier="calendar-staff-filter"
              >
                {isFiltered ? (
                  <Trans>Prikaži vse zaposlene</Trans>
                ) : (
                  <Trans>Prikaži samo tega zaposlenega</Trans>
                )}
              </DropdownItem>
              {canAddAbsence ? (
                <DropdownItem
                  key="absence"
                  data-identifier="calendar-staff-add-absence"
                >
                  <Trans>Dodaj odsotnost</Trans>
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        </div>
      </Tooltip>

      {showName && user.name ? (
        <p
          className="w-full truncate px-1 text-center font-medium text-[11px] text-default-700 leading-tight contain-inline-size"
          data-identifier="calendar-staff-name"
        >
          {user.name}
        </p>
      ) : null}

      {absence != null ? (
        // Kept out of the cell's intrinsic width, or a long badge widens the header past the columns below.
        <div className="flex w-full justify-center contain-inline-size">
          <AbsenceBadge
            row={absence.rows[0]}
            description={describeDayAbsence(absence, shifts, date)}
            date={date}
          />
        </div>
      ) : (
        reservesBadgeSlot && <div aria-hidden className="h-4" />
      )}
    </div>
  );
}

function badgeText(name: string, description: DayAbsenceDescription): string {
  switch (description.kind) {
    case "until": {
      const until = dayjs(description.date).format("D. M.");
      return t`${name} do ${until}`;
    }
    case "allDay":
      return t`${name} ves dan`;
    case "from": {
      const { time } = description;
      return t`${name} od ${time}`;
    }
    case "to": {
      const { time } = description;
      return t`${name} do ${time}`;
    }
    case "between": {
      const { from, to } = description;
      return t`${name} ${from}–${to}`;
    }
    case "partDay":
      return name;
  }
}

function AbsenceBadge({
  row,
  description,
  date,
}: {
  row: CalendarTimeOff;
  description: DayAbsenceDescription;
  date: string;
}) {
  const color = row.typeColor ?? UNTYPED_TYPE_COLOR;
  const name = resolveTimeOffTypeName({
    name: row.typeName,
    systemKey: row.typeSystemKey,
  });
  const text = badgeText(name, description);
  const openTimeOff = useOpenTimeOff();

  return (
    <button
      type="button"
      data-identifier="time-off-header-badge"
      title={text}
      className="h-4 max-w-full truncate rounded-full px-1.5 font-semibold text-[10px] text-white leading-4"
      style={{ backgroundColor: color }}
      onClick={() => openTimeOff(row, date)}
    >
      {text}
    </button>
  );
}
